import React from 'react';
import { Smartphone, Layers, Clock } from 'lucide-react';

/**
 * Компонент для ввода основных параметров AR-проекта
 */
const ProjectParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  projectTypes,
  projectScopes
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      {/* Тип проекта */}
      <div>
        <label htmlFor="projectType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Smartphone className="w-4 h-4 mr-2 text-indigo-500" />
          Тип AR-проекта
        </label>
        <select
          id="projectType"
          name="projectType"
          value={formData.projectType}
          onChange={handleInputChange}
          className={`form-select w-full ${errors.projectType ? 'input-error' : ''}`}
        >
          {projectTypes.map((type) => (
            <option key={type.id} value={type.id}>
              {type.name}
            </option>
          ))}
        </select>
        {errors.projectType && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.projectType}
          </p>
        )}
      </div>

      {/* Объем проекта */}
      <div>
        <label htmlFor="projectScope" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Layers className="w-4 h-4 mr-2 text-indigo-500" />
          Объем проекта
        </label>
        <select
          id="projectScope"
          name="projectScope"
          value={formData.projectScope}
          onChange={handleInputChange}
          className={`form-select w-full ${errors.projectScope ? 'input-error' : ''}`}
        >
          {projectScopes.map((scope) => (
            <option key={scope.id} value={scope.id}>
              {scope.name} - {scope.description}
            </option>
          ))}
        </select>
        {errors.projectScope && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.projectScope}
          </p>
        )}
      </div>

      {/* Количество платформ */}
      <div>
        <label htmlFor="platformCount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Количество платформ
        </label>
        <input
          type="number"
          id="platformCount"
          name="platformCount"
          value={formData.platformCount}
          onChange={handleInputChange}
          min="1"
          max="10"
          className={`form-input w-full ${errors.platformCount ? 'input-error' : ''}`}
          placeholder="Введите количество"
        />
        {errors.platformCount && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.platformCount}
          </p>
        )}
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          iOS, Android, Web и другие платформы
        </p>
      </div>

      {/* Количество функций */}
      <div>
        <label htmlFor="featureCount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Количество AR-функций
        </label>
        <input
          type="number"
          id="featureCount"
          name="featureCount"
          value={formData.featureCount}
          onChange={handleInputChange}
          min="1"
          max="30"
          className={`form-input w-full ${errors.featureCount ? 'input-error' : ''}`}
          placeholder="Введите количество"
        />
        {errors.featureCount && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.featureCount}
          </p>
        )}
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Распознавание объектов, маркеров, лиц, жестов и т.д.
        </p>
      </div>

      {/* Срок разработки */}
      <div className="md:col-span-2">
        <label htmlFor="timelineMonths" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Clock className="w-4 h-4 mr-2 text-indigo-500" />
          Срок разработки (месяцев)
        </label>
        <input
          type="number"
          id="timelineMonths"
          name="timelineMonths"
          value={formData.timelineMonths}
          onChange={handleInputChange}
          min="1"
          max="36"
          className={`form-input w-full md:w-1/3 ${errors.timelineMonths ? 'input-error' : ''}`}
          placeholder="Введите срок"
        />
        {errors.timelineMonths && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.timelineMonths}
          </p>
        )}
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Примечание: Сокращенные сроки могут увеличить стоимость проекта на 20-50%
        </p>
      </div>

      {/* Блок с дополнительной информацией */}
      <div className="md:col-span-2 mt-2 bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Типичные примеры объемов проектов:</h3>
        <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1 list-disc list-inside">
          <li><strong>Малый:</strong> AR-приложение для сканирования одного типа товаров, простая визуализация</li>
          <li><strong>Средний:</strong> AR-каталог продукции с возможностью примерки или размещения в пространстве</li>
          <li><strong>Большой:</strong> Комплексное AR-приложение с несколькими взаимосвязанными функциями, кастомизацией и аналитикой</li>
          <li><strong>Корпоративный:</strong> Масштабное решение с интеграцией в корпоративные системы, множеством функций и высокой детализацией</li>
        </ul>
      </div>
    </div>
  );
};

export default ProjectParametersSection;